import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Clock, User, Globe, Search, Loader, Download } from 'lucide-react';
import Header from '../../components/Header';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import apiService from '../../utils/apiService';
import { generateExcelReport } from '../../utils/excelGenerator';
import { generatePdfReport } from '../../utils/pdfGenerator';

const AccessHistoryPage = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [accessLogs, setAccessLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAccessLogs = async () => {
      try {
        setLoading(true);
        const response = await apiService.getStats();
        
        if (response.success && response.stats.recentUsers) {
          // Transformar datos de usuarios a formato de logs de acceso
          const logs = response.stats.recentUsers.map((user, index) => ({
            id: user.id,
            userId: user.id,
            userName: user.name,
            email: user.email,
            timestamp: user.last_login ? new Date(user.last_login).toLocaleString('es-ES') : 'Nunca',
            ipAddress: 'N/A', // No tenemos IP en el schema actual
            device: 'N/A', // No tenemos device en el schema actual
            location: user.agency || 'Sin agencia',
            role: user.role || 'Usuario'
          }));
          setAccessLogs(logs);
        }
      } catch (error) {
        console.error('Error cargando historial de accesos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAccessLogs();
    
    // Escuchar cambios de tenant
    window.addEventListener('tenantChanged', fetchAccessLogs);
    return () => window.removeEventListener('tenantChanged', fetchAccessLogs);
  }, []);

  if (!isAdmin) {
    navigate('/admin');
    return null;
  }

  const filteredLogs = accessLogs.filter(log =>
    log.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    log.location.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const exportAccessLogsPdf = () => {
    const headers = ['Usuario', 'Email', 'Rol', 'Último Acceso', 'Agencia'];
    const rows = filteredLogs.map(log => [
      log.userName,
      log.email,
      log.role === 'admin' ? 'Administrador' : 'Usuario',
      log.timestamp,
      log.location
    ]);
    generatePdfReport('Historial de Accesos', headers, rows, 'Historial_de_Accesos');
  };

  const exportAccessLogsExcel = () => {
    const headers = ['Usuario', 'Email', 'Rol', 'Último Acceso', 'Agencia'];
    const rows = filteredLogs.map(log => [
      log.userName,
      log.email,
      log.role === 'admin' ? 'Administrador' : 'Usuario',
      log.timestamp,
      log.location
    ]);
    generateExcelReport('Historial_de_Accesos', headers, rows);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <Header />
        <div className="flex items-center justify-center h-96">
          <Loader className="w-12 h-12 text-blue-600 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Historial de Accesos</h2>
          <p className="text-xl text-gray-600">Monitorea los inicios de sesión y la actividad de los usuarios.</p>
        </motion.div>

        <motion.div
          className="bg-white/80 backdrop-blur-xl rounded-2xl p-6 mb-8 shadow-lg border border-white/20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <div className="flex justify-between items-center mb-6">
            <div className="relative flex-1 max-w-md">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar accesos por usuario o agencia..."
                className="w-full pl-12 pr-4 py-3 bg-gray-50/80 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 transition-all duration-300"
              />
            </div>
            <div className="flex gap-2 ml-4">
              <motion.button
                onClick={exportAccessLogsPdf}
                className="p-2 bg-red-100 rounded-full text-red-600 hover:bg-red-200 transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                title="Descargar PDF"
              >
                <Download className="w-5 h-5" />
              </motion.button>
              <motion.button
                onClick={exportAccessLogsExcel}
                className="p-2 bg-green-100 rounded-full text-green-600 hover:bg-green-200 transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                title="Descargar Excel"
              >
                <Download className="w-5 h-5" />
              </motion.button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-xl shadow-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 uppercase text-sm leading-normal">
                  <th className="py-3 px-6 text-left">Usuario</th>
                  <th className="py-3 px-6 text-left">Email</th>
                  <th className="py-3 px-6 text-left">Rol</th>
                  <th className="py-3 px-6 text-left">Último Acceso</th>
                  <th className="py-3 px-6 text-left">Agencia</th>
                </tr>
              </thead>
              <tbody className="text-gray-700 text-sm font-light">
                {filteredLogs.length > 0 ? (
                  filteredLogs.map((log) => (
                    <motion.tr
                      key={log.id}
                      className="border-b border-gray-200 hover:bg-gray-50"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      <td className="py-4 px-6 text-left whitespace-nowrap">
                        <div className="flex items-center">
                          <User className="w-4 h-4 text-blue-500 mr-2" />
                          <span className="font-medium">{log.userName}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6 text-left">{log.email}</td>
                      <td className="py-4 px-6 text-left">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                          log.role === 'admin' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'
                        }`}>
                          {log.role === 'admin' ? 'Administrador' : 'Usuario'}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-left">
                        <div className="flex items-center">
                          <Clock className="w-4 h-4 text-gray-400 mr-2" />
                          {log.timestamp}
                        </div>
                      </td>
                      <td className="py-4 px-6 text-left">
                        <div className="flex items-center">
                          <Globe className="w-4 h-4 text-gray-400 mr-2" />
                          {log.location}
                        </div>
                      </td>
                    </motion.tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="py-8 text-center text-gray-500">
                      No se encontraron registros de acceso
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filteredLogs.length === 0 && (
            <motion.div
              className="text-center py-12 text-gray-500"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              No se encontraron registros de acceso que coincidan con tu búsqueda.
            </motion.div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default AccessHistoryPage;